import React, { useEffect, useState, } from 'react';

import {
  Typography,
  makeStyles,
  Grid,
  TableCell,
  TableRow,
  Dialog,
  DialogContent,
  IconButton,
  DialogTitle,
} from '@material-ui/core';

import ToastAnimated, { showToast } from '../../ui-lib/toast';
import 'react-toastify/dist/ReactToastify.css';

import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';

import ConfirmDialog from '../ConfirmDialog';
import Table from '../Table';
import TypeOptionForm from './TypeOptionForm';

import useModal from '../../hooks/useModal';


const useStyles = makeStyles({
  sectionHeader: {
    color: '#1C265A',
    fontWeight: 'bolder',
    fontSize: '1.2rem',
    marginBottom: '2rem',
  },
});


/**
 * @param {string} title
 * @param {boolean} open
 * @param {() => Promise<Object[]>} onFetch - fetch callback
 * @param {(optionId: string | number) => Promise<void>} onDelete - delete callback
 * @param {(optionData: Object) => Promise<Object>} onUpdate - update callback
 * @param {(optionData: Object) => Promise<Object>} onCreate - create callback
 * @param {() => void} onClose - close modal callback
 */
function TypeOptionsModal({
  open,
  title,
  onFetch,
  onDelete,
  onUpdate,
  onCreate,
  onClose,
  idKey = 'id',
}) {
  const classes = useStyles();
  const HEADERS = [
    {
      label: 'Nome',
      align: 'center',
    },
    {
      label: '',
      align: 'right',
    },
  ];
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [optionId, setOptionId] = useState(undefined);
  const currOption = options.find((item) => item[idKey] == optionId);
  const deleteModal = useModal((optionId) => setOptionId(optionId), () => setOptionId(undefined));


  async function _onDelete() {
    const id = optionId;
    deleteModal.onClose();
    if (undefined == id) {
      return;
    }

    try {
      await onDelete(id);
      setOptions(options.filter((item) => id != item[idKey]));
      showToast({
        type: 'success',
        message: 'Excluído com sucesso',
      });
    } catch {
      showToast({
        type: 'error',
        message: 'Falha ao excluir',
      });
    }
  }

  async function _onFetch() {
    setLoading(true);
    try {
      const options = await onFetch();
      setOptions(options);
    } catch (err) {
      showToast({
        type: 'error',
        message: 'Falha ao carregar',
      });
    }
    setLoading(false);
  }

  async function _onUpdate(data) {
    try {
      const updatedOption = await onUpdate(data);
      const updatedOptions = [...options];
      const idx = updatedOptions.findIndex((item) => optionId == item[idKey]);
      if (idx > -1) {
        updatedOptions[idx] = updatedOption;
      }
      setOptions(updatedOptions);
      showToast({
        type: 'success',
        message: 'Atualizado com sucesso',
      });
      setOptionId(undefined);
      return true;
    } catch {
      showToast({
        type: 'error',
        message: 'Falha ao atualizar',
      });
      return false;
    }
  }

  async function _onCreate(data) {
    try {
      const createdOption = await onCreate(data);
      setOptions([...options, createdOption]);
      showToast({
        type: 'success',
        message: 'Criado com sucesso',
      });
      return true;
    } catch {
      showToast({
        type: 'error',
        message: 'Falha ao criar',
      });
      return false;
    }
  }

  async function onSubmitForm(data) {
    return await (undefined != optionId ? _onUpdate(data) : _onCreate(data));
  }

  function clearSelectedOption() {
    setOptionId(undefined);
  }

  function onCancel() {
    clearSelectedOption();
  }

  function _onClose() {
    clearSelectedOption();
    onClose();
  }

  useEffect(() => {
    if (open) {
      _onFetch();
    }
  }, [open]);


  return (
    <Dialog
      open={open}
      onClose={_onClose}
      maxWidth="lg"
      PaperProps={{
        style: {
          width: '800px',
          maxWidth: '90%',
        }
      }}
    >
      <ToastAnimated />

      <DialogTitle
        style={{
          backgroundColor: '#3B66FF',
          color: 'white',
        }}
      >
        { title }
      </DialogTitle>

      <DialogContent>
        <Grid
          container
          justifyContent="space-around"
        >
          <Grid
            item
            md={5}
            xs={12}
            style={{
              paddingRight: '1.0rem',
            }}
          >
            <Typography
              variant="h6"
              className={classes.sectionHeader}
            >
              Configurar
            </Typography>
            <TypeOptionForm
              data={deleteModal.value ? undefined : currOption}
              onCancel={onCancel}
              onSubmit={onSubmitForm}
            />
          </Grid>

          <Grid
            item
            md={7}
            xs={12}
          >
            <Typography
              variant="h6"
              className={classes.sectionHeader}
            >
              Todos as opções
            </Typography>
            <Table
              headers={HEADERS}
              data={options}
              loading={loading}
              disablePagination
              errorMessage="Falha ao carregar"
              noDataMessage="Nenhum item encontrado"
              maxHeight={300}
            >
              {
                (option, props) => {
                  return (
                    <TableRow {...props}>
                      <TableCell align="center">
                        { option.name }
                      </TableCell>
                      <TableCell
                        align="right"
                        size="small"
                      >
                        <IconButton
                          size="small"
                          onClick={() => setOptionId(option[idKey])}
                        >
                          <EditIcon />
                        </IconButton>

                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => deleteModal.onOpen(option[idKey])}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                }
              }
            </Table>
          </Grid>
        </Grid>

        <ConfirmDialog
          open={deleteModal.value}
          onClose={deleteModal.onClose}
          onConfirm={_onDelete}
          text="Tem certeza que deseja excluir o recurso?"
          secondaryText="O recurso será excluído permanentemente"
          confirmationButtonLabel="Excluir"
          buttonBackgroundColor="red"
        />
      </DialogContent>
    </Dialog>
  );
}

export default TypeOptionsModal;
